import React from "react";
import { Box, Container, Typography, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";

/**
 * Dark bridge section between How It Works and What's Different. Frames the
 * "why data as an input" thesis via a real Smita quote from EdTech Insiders,
 * anchored by the 40%-testing-below-grade stat.
 */
const DataProblem = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 10, md: 16 },
        bgcolor: theme.palette.text.primary,
        color: theme.palette.paper.primary,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Ambient green radial glow */}
      <Box
        sx={{
          position: "absolute",
          top: "-200px",
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 900,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(32,156,51,0.15) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="md"
        sx={{ position: "relative", zIndex: 1, textAlign: "center" }}
      >
        <Typography
          sx={{
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: theme.palette.success.main,
            fontWeight: 700,
            mb: 3,
          }}
        >
          Why data-as-input, not homework
        </Typography>

        <Typography
          component="h2"
          sx={{
            fontSize: { xs: "5rem", md: "8rem" },
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 0.95,
            color: theme.palette.success.main,
          }}
        >
          40%
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "1rem", md: "1.1875rem" },
            color: "rgba(255,255,255,0.75)",
            mt: 2,
            mb: { xs: 6, md: 8 },
            maxWidth: 520,
            mx: "auto",
            lineHeight: 1.5,
          }}
        >
          of the classroom is testing 3+ grade levels below in the Title I
          and charter schools Maestro works with.
        </Typography>

        {/* Large open quote glyph */}
        <Typography
          aria-hidden
          sx={{
            fontSize: { xs: "5rem", md: "7rem" },
            fontFamily: "Georgia, serif",
            lineHeight: 0,
            color: theme.palette.success.main,
            opacity: 0.5,
            mb: 3,
            mt: 3,
          }}
        >
          &ldquo;
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "1.375rem", md: "1.875rem" },
            fontWeight: 400,
            lineHeight: 1.4,
            color: theme.palette.paper.primary,
            fontStyle: "italic",
            maxWidth: 780,
            mx: "auto",
            textWrap: "balance",
          }}
        >
          You already have such a busy day with your students, and you're doing
          lesson planning, and suddenly you need to context-switch and
          understand your data with very limited time, and then figure out what
          to do with it. Expecting teachers to do that on a daily basis just
          didn't feel doable.
        </Typography>

        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: theme.palette.success.main,
            mt: 4,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Smita Saxena · Founder, Maestro
        </Typography>

        {/* CTA — outlined green pill on dark, more prominent than an inline link */}
        <Box
          component={RouterLink}
          to={getPageRoute("home", "FOUNDER_NOTE")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            mt: { xs: 6, md: 8 },
            px: { xs: 3, md: 4 },
            py: { xs: 1.75, md: 2 },
            fontSize: { xs: "0.9375rem", md: "1.0625rem" },
            fontWeight: 700,
            color: "#4ade80",
            textDecoration: "none",
            bgcolor: "rgba(34, 197, 94, 0.08)",
            border: "1.5px solid rgba(74, 222, 128, 0.5)",
            borderRadius: 999,
            boxShadow: "0 0 32px rgba(34,197,94,0.15)",
            transition:
              "background-color 0.2s, border-color 0.2s, transform 0.15s, box-shadow 0.2s",
            "&:hover": {
              bgcolor: "rgba(34, 197, 94, 0.15)",
              borderColor: "#4ade80",
              transform: "translateY(-2px)",
              boxShadow: "0 0 40px rgba(34,197,94,0.25)",
            },
            "&:hover .arrow": {
              transform: "translateX(4px)",
            },
          }}
        >
          Read the full founder story
          <Icon
            baseClassName="material-symbols-outlined"
            className="arrow"
            sx={{
              fontSize: 22,
              transition: "transform 0.15s",
            }}
          >
            arrow_forward
          </Icon>
        </Box>
      </Container>
    </Box>
  );
};

export default DataProblem;
